import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import StartIcon from '@mui/icons-material/Start';
import {
    Box,
    Typography,
    useTheme
} from '@mui/material';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import { useAppContext } from '../../context/AppContext';
import { LessonPlanSlideScripts } from '../../object-actions/types/types';

interface ScriptsDisplayProps {
    scripts: LessonPlanSlideScripts;
}

const ScriptsDisplay: React.FC<ScriptsDisplayProps> = ({ scripts }) => {
    const theme = useTheme();
    const { membership } = useAppContext();
    const availableRoles = membership?.roles || [];

    const scriptTypes = [
        { key: 'on_neutral' as const, label: 'Reframe On Neutral Response', icon: <SentimentNeutralIcon color="disabled" />, color: 'default' as const },
        { key: 'on_negative' as const, label: 'Reframe On Negative Response', icon: <SentimentDissatisfiedIcon color="error" />, color: 'error' as const },
        { key: 'on_positive' as const, label: 'Reframe On Positive Response', icon: <SentimentSatisfiedIcon color="success" />, color: 'success' as const },
    ];

    const hasOpeningScript = scripts.open && scripts.open.trim();
    const responseScripts = scriptTypes.filter(st => scripts[st.key] && scripts[st.key].trim());

    if (!hasOpeningScript && responseScripts.length === 0) {
        return null;
    }

    return (
        <Box sx={{ mt: 3 }}>
            <Box
                sx={{
                    backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.02)',
                    borderRadius: 1,
                    p: 2,
                }}
            >
                {/* Opening Script */}
                {hasOpeningScript && (
                    <Box sx={{ mb: responseScripts.length > 0 ? 2 : 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', alignContent: 'center', gap: 1.5 }}>
                            <StartIcon color="primary" />
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Opening Question</Typography>
                        </Box>
                        <ReactMarkdown>{scripts.open}</ReactMarkdown>
                    </Box>
                )}

                {/* Response Scripts - Nested and Indented */}
                {responseScripts.length > 0 && availableRoles.includes('coach') && (
                    <Box sx={{ pl: hasOpeningScript ? 2 : 0, borderLeft: hasOpeningScript ? `2px solid ${theme.palette.divider}` : 'none', ml: hasOpeningScript ? 2 : 0 }}>
                        {responseScripts.map((scriptType) => {
                            const scriptContent = scripts[scriptType.key];
                            if (!scriptContent || !scriptContent.trim()) {
                                return null;
                            }

                            return (
                                <Box key={scriptType.key} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        {scriptType.icon}
                                        <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                            {scriptType.label}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ pl: 3 }}>
                                        <ReactMarkdown>{scriptContent}</ReactMarkdown>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ScriptsDisplay;
